import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { bySortIndex } from '../../lib/sortIndex';
import type { Id, Profile } from '../../lib/types';
import { useDeviceStore } from '../../store/device';
import { useSyncedStore } from '../../store/synced';
import { pickEntryRoute } from './pickEntryRoute';

// Persistent profile-switcher per plan/03 §B. Bottom-left avatar button on
// every kid-facing screen; tapping it pops a small box of all profiles right
// above it. Picking a profile lands on its routine selector (per plan: "not
// the same routine — the routine they'd want next is theirs"). Hidden
// entirely when there's only one profile — nothing to switch to.
export function QuickSwitcher() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const tree = useSyncedStore((s) => s.tree);
  const activeProfileId = useDeviceStore((s) => s.activeProfileId);

  const profiles = (tree?.profiles ?? []).slice().sort(bySortIndex);
  const activeProfile = profiles.find((p) => p.id === activeProfileId);

  if (!activeProfile || profiles.length <= 1) return null;

  const switchTo = async (profileId: Id) => {
    setOpen(false);
    const target = profiles.find((p) => p.id === profileId);
    if (!target || !tree) return;
    // If the picked profile has exactly one routine in progress today, skip
    // the routine selector and land on it directly.
    navigate(await pickEntryRoute(tree, target));
  };

  return (
    <>
      <button
        type="button"
        className="quick-switch-button"
        aria-label={`Vaihda profiilia (nyt: ${activeProfile.name})`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        style={{ background: activeProfile.color }}
      >
        <span aria-hidden="true">{activeProfile.avatarEmoji}</span>
      </button>
      {open && (
        <div className="quick-switch-sheet" role="dialog" aria-label="Vaihda profiilia">
          <button
            type="button"
            className="quick-switch-backdrop"
            aria-label="Sulje"
            onClick={() => setOpen(false)}
          />
          <ul className="quick-switch-strip">
            {profiles.map((p) => (
              <li key={p.id}>
                <ProfileChip
                  profile={p}
                  active={p.id === activeProfileId}
                  onPick={() => switchTo(p.id)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function ProfileChip({
  profile,
  active,
  onPick,
}: {
  profile: Profile;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      className={`quick-switch-card${active ? ' active' : ''}`}
      onClick={onPick}
      style={{ background: profile.color }}
      aria-pressed={active}
    >
      <span className="quick-switch-avatar" aria-hidden="true">
        {profile.avatarEmoji}
      </span>
      <span className="quick-switch-name">{profile.name}</span>
    </button>
  );
}
